/**
 * Form components that add and remove rows client-side: generic dynamic
 * lists (test-case steps, QA checks, key controls), the project
 * environments/personas editor, and the per-persona credential fields.
 *
 * Markup contracts:
 *   dynamic_list — root may carry data-row-label="Step"; x-ref="rows" holds
 *     [data-row] rows; a <template> that is a direct child of the root is the
 *     row source and may use the __INDEX__ token. With data-row-label,
 *     [data-row-number] text and the first input's aria-label are renumbered
 *     on every change.
 *   environments_form — root is the Environments fieldset with x-ref="list"
 *     and x-ref="defaultInput"; rows are .env-row[data-env-idx]; the document
 *     renders #env-row-template (__ENV_INDEX__, __ENV_DISPLAY__) and
 *     #persona-row-template (__ENV_INDEX__, __P_INDEX__). On every reorder a
 *     row's <legend> and its aria-labels starting "Environment N" /
 *     "Set environment N" are renumbered along with its field names and ids.
 *   persona_credentials — root may carry data-password-stored="true";
 *     x-ref="authMethod" is the auth-method <select> bound with x-model.
 */
import { cloneTemplate, requireTemplate } from "../template-clone.js";
import { describe_el } from "./helpers.js";

function rowOf(event, selector) {
  const row = event.currentTarget.closest(selector);
  if (!row) throw new Error(`${describe_el(event.currentTarget)} is not inside ${selector}`);
  return row;
}

function focusFirstInput(row) {
  const input = row.querySelector("input, select, textarea");
  if (input) input.focus();
}

function renumberRows(rows, rowLabel) {
  if (!rowLabel) return;
  rows.querySelectorAll("[data-row]").forEach((row, i) => {
    const n = String(i + 1);
    const number = row.querySelector("[data-row-number]");
    if (number) number.textContent = n;
    const input = row.querySelector("input");
    if (input) input.setAttribute("aria-label", `${rowLabel} ${n}`);
  });
}

function reindexEnvList(list, defaultInput) {
  list.querySelectorAll(".env-row").forEach((row, i) => {
    reindexEnvRow(row, i);
  });
  const checked = list.querySelector('input[name="env_default_radio"]:checked');
  if (checked) defaultInput.value = checked.value;
}

// Field names carry the environment index as their last `_N` segment; any
// name shaped that way is env-indexed, so a new env-scoped field is renumbered
// without extending an allowlist — paired with _env_row.html and its includes.
const ENV_INDEXED_NAME = /_(\d+)$/;
// Element ids and label targets carry it as an `env-N-` prefix.
const ENV_INDEXED_ID = /^env-\d+-/;
// Persona fields end `_<env>_<persona>`; ids/labels start `persona-<env>-<persona>-`.
// These patterns are paired with the persona-row template: a field added there
// without the suffix silently keeps its original indices after a reorder.
const PERSONA_INDEXED_NAME = /_\d+_\d+$/;
const PERSONA_INDEXED_ID = /^persona-\d+-\d+-/;
// Paired with the `env_display` strings _env_row.html renders: the legend and
// the aria-labels that name the environment by its 1-based position.
const ENV_DISPLAY_TEXT = /^(Set )?([Ee]nvironment) \d+\b/;

function reindexPersonaRows(envRow, envIdx) {
  const rows = envRow.querySelectorAll("[data-persona-row]");
  rows.forEach((row, pIdx) => {
    row.dataset.personaIdx = String(pIdx);
    for (const field of row.querySelectorAll("input[name], select[name]")) {
      field.name = field.name.replace(PERSONA_INDEXED_NAME, `_${envIdx}_${pIdx}`);
    }
    for (const el of row.querySelectorAll('[id^="persona-"]')) {
      el.id = el.id.replace(PERSONA_INDEXED_ID, `persona-${envIdx}-${pIdx}-`);
    }
    for (const label of row.querySelectorAll('label[for^="persona-"]')) {
      label.htmlFor = label.htmlFor.replace(PERSONA_INDEXED_ID, `persona-${envIdx}-${pIdx}-`);
    }
  });
  const count = envRow.querySelector(".persona-count");
  if (count) {
    count.name = `persona_count_${envIdx}`;
    count.value = String(rows.length);
  }
  const heading = envRow.querySelector("details > summary h3");
  if (heading) heading.textContent = `Personas (${rows.length})`;
}

function reindexEnvRow(row, envIdx) {
  row.dataset.envIdx = String(envIdx);
  const radio = row.querySelector('input[name="env_default_radio"]');
  if (radio) radio.value = String(envIdx);
  for (const field of row.querySelectorAll("input[name], select[name], textarea[name]")) {
    // Persona fields also end `_<n>` (their persona index) and are reindexed
    // separately by reindexPersonaRows — skip them here or the trailing
    // persona index would be clobbered with the environment index.
    if (PERSONA_INDEXED_NAME.test(field.name)) continue;
    field.name = field.name.replace(ENV_INDEXED_NAME, `_${envIdx}`);
  }
  for (const el of row.querySelectorAll('[id^="env-"]')) {
    el.id = el.id.replace(ENV_INDEXED_ID, `env-${envIdx}-`);
  }
  for (const label of row.querySelectorAll('label[for^="env-"]')) {
    label.htmlFor = label.htmlFor.replace(ENV_INDEXED_ID, `env-${envIdx}-`);
  }
  const display = String(envIdx + 1);
  const legend = row.querySelector("legend");
  if (legend) legend.textContent = legend.textContent.replace(ENV_DISPLAY_TEXT, `$1$2 ${display}`);
  for (const el of row.querySelectorAll(
    '[aria-label^="Environment "], [aria-label^="Set environment "]',
  )) {
    el.setAttribute(
      "aria-label",
      el.getAttribute("aria-label").replace(ENV_DISPLAY_TEXT, `$1$2 ${display}`),
    );
  }
  const personas = row.querySelector(".personas-list");
  if (personas) personas.dataset.envIdx = String(envIdx);
  reindexPersonaRows(row, envIdx);
}

document.addEventListener("alpine:init", () => {
  Alpine.data("dynamic_list", () => ({
    _template: null,
    _rowLabel: "",
    init() {
      // :scope > template — a nested x-for template inside a row must not be
      // picked up as the row source.
      this._template = this.$el.querySelector(":scope > template");
      if (!this._template) {
        throw new Error(`dynamic_list: no <template> inside ${describe_el(this.$el)}`);
      }
      this._rowLabel = this.$el.dataset.rowLabel || "";
    },
    addRow() {
      const rows = this.$refs.rows;
      const count = rows.querySelectorAll("[data-row]").length;
      const row = cloneTemplate(this._template, { __INDEX__: String(count + 1) });
      rows.appendChild(row);
      renumberRows(rows, this._rowLabel);
      focusFirstInput(row);
    },
    removeRow(event) {
      // Alpine resolves `$refs` by walking up from the element whose handler
      // is running, so every ref this needs must be resolved before the row
      // detaches — afterwards the walk never reaches the root.
      const rows = this.$refs.rows;
      rowOf(event, "[data-row]").remove();
      renumberRows(rows, this._rowLabel);
    },
  }));

  Alpine.data("environments_form", () => ({
    addEnv() {
      const list = this.$refs.list;
      const idx = list.querySelectorAll(".env-row").length;
      const row = cloneTemplate(requireTemplate("env-row-template"), {
        __ENV_INDEX__: String(idx),
        __ENV_DISPLAY__: String(idx + 1),
      });
      list.appendChild(row);
      Alpine.initTree(row);
      reindexEnvList(list, this.$refs.defaultInput);
      focusFirstInput(row);
    },
    removeEnv(event) {
      // Same as removeRow: resolve every $refs lookup before the row detaches.
      const list = this.$refs.list;
      const defaultInput = this.$refs.defaultInput;
      const row = rowOf(event, ".env-row");
      const wasDefault = row.querySelector('input[name="env_default_radio"]')?.checked;
      row.remove();
      if (wasDefault) {
        const first = list.querySelector('input[name="env_default_radio"]');
        if (first) first.checked = true;
      }
      reindexEnvList(list, defaultInput);
    },
    setDefault(event) {
      if (event.target.checked) this.$refs.defaultInput.value = event.target.value;
    },
    addPersona(event) {
      const envRow = rowOf(event, ".env-row");
      const list = envRow.querySelector(".personas-list");
      const envIdx = envRow.dataset.envIdx;
      const pIdx = list.querySelectorAll("[data-persona-row]").length;
      const row = cloneTemplate(requireTemplate("persona-row-template"), {
        __ENV_INDEX__: envIdx,
        __P_INDEX__: String(pIdx),
      });
      list.insertBefore(row, list.querySelector(".persona-count"));
      Alpine.initTree(row);
      reindexPersonaRows(envRow, Number(envIdx));
      focusFirstInput(row);
    },
    removePersona(event) {
      const envRow = rowOf(event, ".env-row");
      rowOf(event, "[data-persona-row]").remove();
      reindexPersonaRows(envRow, Number(envRow.dataset.envIdx));
    },
  }));

  Alpine.data("persona_credentials", () => ({
    authMethod: "default",
    passwordStored: false,
    init() {
      this.authMethod = this.$refs.authMethod.value;
      this.passwordStored = this.$el.dataset.passwordStored === "true";
    },
    get hideUsername() {
      return this.authMethod !== "returning";
    },
    get hidePassword() {
      return this.authMethod === "default";
    },
    get hideApiToken() {
      return this.authMethod === "default";
    },
    get hidePrefix() {
      return this.authMethod !== "ephemeral";
    },
    get passwordPlaceholder() {
      if (this.passwordStored) return "(unchanged)";
      return this.authMethod === "ephemeral" ? "Registration password" : "Password";
    },
  }));
});
